/**
 * Debounced search input. Commits the value to `onChange` after 300 ms of
 * idle typing so pages don't refetch on every keystroke. Also exposes a
 * "clear" button.
 */

import React, { useEffect, useRef, useState } from 'react';
import Input from '@amzn/meridian/input';

export interface SearchInputProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  debounceMs?: number;
  ariaLabel?: string;
}

export const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  placeholder = 'Search…',
  debounceMs = 300,
  ariaLabel,
}) => {
  const [local, setLocal] = useState(value);

  // Keep local state in sync when the parent resets the value (e.g., URL
  // navigation back to an empty filter).
  useEffect(() => {
    setLocal(value);
  }, [value]);

  // Stash the latest ``onChange`` in a ref so the debounce timer always
  // invokes the current callback without having to re-run the debounce
  // effect when the parent re-renders. Without this, non-memoized
  // callbacks would either fire a stale closure or restart the timer on
  // every parent render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    if (local === value) return;
    const handle = setTimeout(() => onChangeRef.current(local), debounceMs);
    return () => clearTimeout(handle);
  }, [local, debounceMs, value]);

  return (
    <Input
      type="text"
      value={local}
      onChange={setLocal}
      placeholder={placeholder}
      prefixIconTokens={undefined}
      aria-label={ariaLabel || placeholder}
    />
  );
};
